import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { getLeadById, logError } from '../api';
import { isComercial, isAdmin, isPropietario } from '../services/AuthService';
import LeadEventModal from './LeadEventModal';
import LeadEventsRow from './LeadEventsRow';
import LeadEditModal from './LeadEditModal';


const LeadManagementDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showEventModal, setShowEventModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    useEffect(() => {
        if (id) {
            loadLead();
        }
    }, [id]);

    const loadLead = async () => {
        setLoading(true);
        try {
            const response = await getLeadById(id);
            setLead(response);
        } catch (error) {
            logError(error);
            console.error('Error loading lead:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEventAdded = () => {
        loadLead();
    };

    const handleLeadUpdated = () => {
        loadLead();
    };

    const canEditFields = () => {
        return (!lead?.name || lead.name.trim() === '') || (!lead?.zipCode || lead.zipCode.trim() === '');
    };

    const isSuperUser = () => {
        return isAdmin() || isPropietario();
    };

    const handleEditLead = () => {
        window.open(`/lead-assignment/edit/${id}`, '_blank');
    };



    return (
        <Container fluid className="my-4">
            <Row className="mb-4">
                <Col xs={12} md={6}>
                    <h1>Gestión de Lead - {loading ? 'Cargando...' : (lead?.name || 'Sin nombre')}</h1>
                </Col>
                <Col xs={12} md={6} className="d-flex justify-content-end gap-2">
                    {isSuperUser() ? (
                        <Button 
                            variant="warning"
                            size="sm"
                            onClick={handleEditLead}
                        >
                            <i className="bi bi-pencil-fill me-1"></i>
                            Editar Datos
                        </Button>
                    ) : canEditFields() && (
                        <Button 
                            variant="warning"
                            size="sm"
                            onClick={() => setShowEditModal(true)}
                        >
                            <i className="bi bi-pencil-fill me-1"></i>
                            Completar Datos
                        </Button>
                    )}
                    {isComercial() && (
                        <Button 
                            data-testid="create-order-from-lead-btn"
                            variant="success" 
                            onClick={() => navigate('/orders/new', { state: { leadData: lead } })}
                        >
                            Crear Pedido
                        </Button>
                    )}
                    <Button 
                        data-testid="back-btn"
                        variant="secondary" 
                        onClick={() => navigate('/lead-management')}
                    >
                        Volver
                    </Button>
                </Col>
            </Row>

            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            ) : (
                <>
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <Row className="align-items-center">
                                <Col md={8}>
                                    <div className="d-flex align-items-center mb-2">
                                        <div className="d-flex align-items-center me-3">
                                            <h3 className="mb-0 text-primary">
                                                👤 {lead?.name || (
                                                    <span className="text-muted fst-italic">
                                                        Sin nombre
                                                        <i className="bi bi-exclamation-triangle-fill text-warning ms-2"></i>
                                                    </span>
                                                )}
                                            </h3>
                                        </div>
                                        <span className={`badge fs-6 ${lead?.zipCode ? 'bg-secondary' : 'bg-light text-muted border'}`}>
                                            {lead?.zipCode || (
                                                <span>
                                                    Sin código postal
                                                    <i className="bi bi-exclamation-triangle-fill text-warning ms-1"></i>
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="d-flex gap-4 text-muted">
                                        <span>📞 {lead?.phone}</span>
                                        <span>🌐 {lead?.origin?.name || 'Sin origen'}</span>
                                        <span>📊 {
                                            lead?.status?.name ? (
                                                <span 
                                                    className="badge ms-1" 
                                                    style={{ 
                                                        backgroundColor: (() => {
                                                            const colors = {
                                                                1: '#ff69b4', 2: '#9370db', 3: '#20b2aa', 4: '#87ceeb',
                                                                5: '#ffd700', 6: '#dc3545', 7: '#8b0000', 8: '#28a745', 9: '#ff8c00'
                                                            };
                                                            return colors[lead.statusId] || '#6c757d';
                                                        })(), 
                                                        color: 'white' 
                                                    }}
                                                >
                                                    {lead.status.name}
                                                </span>
                                            ) : 'Sin status'
                                        }</span>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="text-center p-3 rounded" style={{ background: 'linear-gradient(135deg, #ffeaa7, #f39c12)', color: '#fff' }}>
                                        <div className="fs-6 fw-bold mb-1">📅 PRÓXIMO SEGUIMIENTO</div>
                                        <div className="fs-4 fw-bold">
                                            {lead?.followUpDate ? new Date(lead.followUpDate).toLocaleDateString() : 'Sin programar'}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {lead?.comments && (
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-info text-white">
                                <h5 className="mb-0">💬 Comentarios</h5>
                            </div>
                            <div className="card-body">
                                <div className="bg-light p-3 rounded border-start border-info border-4">
                                    <p className="mb-0 fs-6" style={{ lineHeight: '1.6' }}>{lead.comments}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="card shadow-sm mb-4" data-testid="registries-section">
                        <div className="card-header bg-warning text-dark">
                            <div className="d-flex align-items-center justify-content-between">
                                <h5 className="mb-0" data-testid="registries-title">📅 Registros ({lead?.leadRegistries?.length || 0})</h5>
                                <Button 
                                    data-testid="add-event-btn"
                                    variant="success" 
                                    size="sm"
                                    onClick={() => setShowEventModal(true)}
                                >
                                    + Añadir Registro
                                </Button>
                            </div>
                        </div>
                        <div className="card-body p-0" data-testid="registries-content">
                            <LeadEventsRow registries={lead?.leadRegistries || []} />
                        </div>
                    </div>
                </>
            )}
            
            <LeadEventModal
                show={showEventModal}
                handleClose={() => setShowEventModal(false)}
                leadId={id}
                onEventAdded={handleEventAdded}
            />
            
            <LeadEditModal
                show={showEditModal}
                handleClose={() => setShowEditModal(false)}
                lead={lead}
                onUpdate={handleLeadUpdated}
            />
        </Container>
    );
};

export default LeadManagementDetail;